import {
  ArrowUpOutlined,
  DeleteOutlined,
  HolderOutlined,
  StopOutlined,
} from '@ant-design/icons'
import { Button, Checkbox, Tag, Tooltip } from 'antd'

import cn from '@/components/StorePriority/ManualPriorityConfig/ManualPriorityConfig.module.scss'
import { MAX_PRIORITY } from '@/constants'

import rowCn from './StorePriorityRow.module.scss'

export type StorePriorityItem = {
  id: string
  code: number
  name: string
  supplierName: string
  isOwn: boolean
  isActive: boolean
  isRemote: boolean
  isMain: boolean
  priority: number
  systemPriority: number
  isExcludedBySupplier?: boolean
  isExcludedByClient?: boolean
  isEffectivelyExcluded?: boolean
}

type StorePriorityRowProps = {
  data: StorePriorityItem
  index: number
  isConfigured: boolean
  isChanged: boolean
  isOverlay?: boolean
  dragProps?: object
  onReset?: (id: string) => void
  onPrioritize?: (id: string) => void
  onToggleSupplierExclusion?: (id: string, value: boolean) => void
  onToggleClientExclusion?: (id: string, value: boolean) => void
  canEditPriority?: boolean
  canEditSupplierExclusion?: boolean
  canEditClientExclusion?: boolean
  showSystemPriority?: boolean
}

export const StorePriorityRow = ({
  data,
  index,
  isConfigured,
  isChanged,
  isOverlay = false,
  dragProps = {},
  onReset,
  onPrioritize,
  onToggleSupplierExclusion,
  onToggleClientExclusion,
  canEditPriority = true,
  canEditSupplierExclusion,
  canEditClientExclusion,
  showSystemPriority = false,
}: StorePriorityRowProps) => (
  <div
    className={`${cn.sortableRow} ${isOverlay ? cn.overlayActive : ''} ${isChanged ? cn.changed : ''} ${!isConfigured ? cn.staticRow : ''}`}
    {...dragProps}
  >
    <div className={cn.dragHandle}>
      <HolderOutlined />
    </div>
    <div className={cn.rowInfo}>
      <div className={cn.nameWrapper}>
        <span className={cn.rowName}>{data.name}</span>
        <span className={cn.rowCount}>#{data.code}</span>
        <span
          className={`${rowCn.statusDot} ${data.isActive ? rowCn.activeStatus : rowCn.inactiveStatus}`}
          title={data.isActive ? 'Активен' : 'Неактивен'}
          aria-label={data.isActive ? 'Активен' : 'Неактивен'}
        />
        {data.isRemote && <Tag>Удалённый</Tag>}
        {!data.isOwn && <Tag>{data.supplierName}</Tag>}
        {data.isEffectivelyExcluded && (
          <Tooltip
            title={`Склад скрыт: ${[
              data.isExcludedBySupplier && 'поставщиком',
              data.isExcludedByClient && 'клиентом',
            ]
              .filter(Boolean)
              .join(' и ')}`}
          >
            <StopOutlined />
          </Tooltip>
        )}
      </div>
      {showSystemPriority && !isConfigured && (
        <span className={cn.rowCount}>
          Системный приоритет:{' '}
          {data.systemPriority === MAX_PRIORITY
            ? 'не установлен'
            : data.systemPriority}
        </span>
      )}
    </div>
    <div
      className={cn.rowActions}
      onPointerDown={(event) => event.stopPropagation()}
      onClick={(event) => event.stopPropagation()}
    >
      {isConfigured ? (
        <>
          <Tag color={isChanged ? 'orange' : 'blue'} className={cn.prioTag}>
            Приоритет: {index + 1}
          </Tag>
          {canEditPriority && (
            <Tooltip title="Сбросить приоритет">
              <Button
                type="text"
                danger
                size="small"
                icon={<DeleteOutlined />}
                onClick={() => onReset?.(data.id)}
              />
            </Tooltip>
          )}
        </>
      ) : canEditPriority ? (
        <Tooltip title="Установить приоритет">
          <Button
            type="text"
            size="small"
            className={cn.addPrioBtn}
            icon={<ArrowUpOutlined />}
            onClick={() => onPrioritize?.(data.id)}
          />
        </Tooltip>
      ) : null}
      {(canEditSupplierExclusion !== undefined ||
        canEditClientExclusion !== undefined) && (
        <div className={rowCn.exclusionControls}>
          <Tooltip title="Кто скрывает склад из выдачи">
            <span className={rowCn.exclusionLabel}>Скрыто:</span>
          </Tooltip>
          {canEditSupplierExclusion !== undefined && (
            <Tooltip
              title={
                data.isExcludedBySupplier
                  ? 'Склад скрыт поставщиком'
                  : 'Склад не скрыт поставщиком'
              }
            >
              <Checkbox
                checked={data.isExcludedBySupplier}
                disabled={!canEditSupplierExclusion}
                onChange={(event) =>
                  onToggleSupplierExclusion?.(data.id, event.target.checked)
                }
              >
                Поставщиком
              </Checkbox>
            </Tooltip>
          )}
          {canEditClientExclusion !== undefined && (
            <Tooltip
              title={
                data.isExcludedByClient
                  ? 'Склад скрыт клиентом'
                  : 'Склад не скрыт клиентом'
              }
            >
              <Checkbox
                checked={data.isExcludedByClient}
                disabled={!canEditClientExclusion}
                onChange={(event) =>
                  onToggleClientExclusion?.(data.id, event.target.checked)
                }
              >
                Клиентом
              </Checkbox>
            </Tooltip>
          )}
        </div>
      )}
    </div>
  </div>
)
